import type { DocVersionSummary } from "@/lib/document/documentTypes";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function shortAuthor(authorId: string | null): string {
  if (!authorId) return "—";
  return authorId.slice(0, 8);
}

type Props = {
  version: DocVersionSummary | null;
  title: string;
};

export function DocumentVersionMetadataCard({ version, title }: Props) {
  if (!version) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 p-4 text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
        Select a version to compare.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 dark:border-zinc-700 dark:bg-zinc-800/40">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
        {title}
      </p>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500 dark:text-zinc-400">Version</dt>
          <dd className="font-medium text-gray-900 dark:text-zinc-100">v{version.version_number}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500 dark:text-zinc-400">Status</dt>
          <dd className="capitalize text-gray-900 dark:text-zinc-100">{version.status}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500 dark:text-zinc-400">Author</dt>
          <dd className="font-mono text-xs text-gray-900 dark:text-zinc-100">
            {shortAuthor(version.author_user_id)}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-gray-500 dark:text-zinc-400">Saved</dt>
          <dd className="text-gray-900 dark:text-zinc-100">{formatWhen(version.created_at)}</dd>
        </div>
        <div>
          <dt className="text-gray-500 dark:text-zinc-400">Change notes</dt>
          <dd className="mt-1 text-gray-900 dark:text-zinc-100">{version.change_notes?.trim() || "—"}</dd>
        </div>
      </dl>
    </div>
  );
}

type CompareProps = {
  versions: DocVersionSummary[];
  leftId: string;
  rightId: string;
  onLeftChange: (id: string) => void;
  onRightChange: (id: string) => void;
};

export function DocumentVersionCompare({
  versions,
  leftId,
  rightId,
  onLeftChange,
  onRightChange,
}: CompareProps) {
  const left = versions.find((v) => v.id === leftId) ?? null;
  const right = versions.find((v) => v.id === rightId) ?? null;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="font-medium text-gray-600 dark:text-zinc-300">Compare A</span>
          <select
            value={leftId}
            onChange={(e) => onLeftChange(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
          >
            <option value="">Select…</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version_number} ({v.status})
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs">
          <span className="font-medium text-gray-600 dark:text-zinc-300">Compare B</span>
          <select
            value={rightId}
            onChange={(e) => onRightChange(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
          >
            <option value="">Select…</option>
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.version_number} ({v.status})
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <DocumentVersionMetadataCard version={left} title="Version A" />
        <DocumentVersionMetadataCard version={right} title="Version B" />
      </div>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Metadata-only compare in V1 — block-level diff is deferred.
      </p>
    </div>
  );
}
